import type { TypeInputState } from '~/types/common.types';

/**
 * [입고 관리] 페이지네이션 관련 상수
 */
export const INITIAL_RECEIVE_PAGE_SIZE = 50;
export const INITIAL_RECEIVE_PAGE_CURSOR = 0;

export const RECEIVE_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export type TypeReceivePageSize = keyof typeof RECEIVE_PAGE_SIZE;

/**
 * [입고 관리] 날짜 검색 최대 기간
 */
export const MAX_RECEIVE_DATE_RANGE = 31;

/**
 * [입고 관리] 입고 방법
 */
export const RELEASE_CODE = {
  KURLY_PICKUP: '컬리픽업',
  PARCEL: '택배',
  DIRECT_DELIVERY: '직배송',
  MILKRUN_PICKUP: '밀크런픽업',
  ETC: '기타',
  KURLY_MILKRUN_PICKUP: '컬리픽업+밀크런픽업',
} as const;

export type TypeReleaseCode = keyof typeof RELEASE_CODE;

/**
 * [입고 관리] 입고 요청 관리 > 입고 상태
 */
export const RECEIVE_REQUEST_STATUS = {
  REQUESTED: '요청완료',
  CONFIRMED: '확인완료',
  CANCELED: '요청취소',
} as const;

export type TypeReceiveRequestStatus = keyof typeof RECEIVE_REQUEST_STATUS;

/**
 * [입고 관리] 입고 요청 관리 > 입고 상태 뱃지 컬러
 */
export const RECEIVE_REQUEST_STATUS_BADGE_COLOR = {
  REQUESTED: 'secondary',
  CONFIRMED: 'info',
  CANCELED: 'error',
} as const;

/**
 * [입고 관리] 입고 현황 > 입고 상태
 * @note 입고요청번호별 작업상태
 */
export const RECEIVE_STATUS_STATUS = {
  PROCEEDING: '입고중',
  COMPLETED: '입고완료',
  FORCED_CLOSED: '강제종료',
} as const;

export type TypeReceiveStatusStatus = keyof typeof RECEIVE_STATUS_STATUS;

/**
 * [입고 관리] 입고 현황 > 입고 상태 뱃지 컬러
 */
export const RECEIVE_STATUS_STATUS_BADGE_COLOR = {
  PROCEEDING: 'primary',
  COMPLETED: 'success',
  FORCED_CLOSED: 'error',
} as const;

/**
 * [입고 관리] 입고 현황 > 입고 현황 상세 > 입고 상태
 * @note 상품별 작업상태
 */
export const RECEIVE_STATUS_DETAIL_STATUS = {
  WAITING: '입고대기',
  PROCEEDING: '입고중',
  COMPLETED: '입고완료',
} as const;

export type TypeReceiveStatusDetailStatus = keyof typeof RECEIVE_STATUS_DETAIL_STATUS;

/**
 * [입고 관리] 입고 현황 > 입고 현황 상세 > 입고 상태 뱃지 컬러
 */
export const RECEIVE_STATUS_DETAIL_STATUS_BADGE_COLOR = {
  WAITING: 'info',
  PROCEEDING: 'primary',
  COMPLETED: 'success',
} as const;

/**
 * [입고 관리 > 입고 요청 관리] 입고 요청 가능한 최대 SKU(품목) 수량
 */
export const MAX_RECEIVE_REQUEST_SKU_COUNT = 200;

/**
 * [입고 관리 > 입고 요청 관리] 입고 요청 가능한 최대 상품 수량
 */
export const MAX_RECEIVE_REQUEST_PRODUCT_COUNT = 20000;

/**
 * [입고 관리] Date 요청 포맷
 */
export const RECEIVE_DATE_FORMAT = {
  DATE_TIME: 'YYYY-MM-DDTHH:mm:ss',
} as const;

/**
 * [입고 관리 > 입고요청관리] 데이터 수정 타입
 */
export const RECEIVE_MODIFICATION_TYPE = {
  MODIFY: 'MODIFY',
  DELETE: 'DELETE',
} as const;

export type TypeReceiveModificationType = keyof typeof RECEIVE_MODIFICATION_TYPE;

/**
 * [입고 관리 > 입고요청관리] 파렛트 사용 여부 옵션값
 */
export const OPTION_PLT = [
  { value: 'false', text: '사용안함' },
  { value: 'true', text: '사용함' },
];

/**
 * [입고 관리 > 입고요청관리] 입고대행서비스 이용 시 경유 센터 코드 (평택: CC03)
 */
export const RECEIVE_X_DOCK_CLUSTER_CODE = 'CC03';

/**
 * [입고 관리] 툴팁 메시지
 */
export const RECEIVE_TOOLTIP_MESSAGE = {
  DATE_USAGE: '유통/소비기한 또는 제조일자가 없다면\n[사용] 체크박스를 해제해 주세요.',
  PALLET_USAGE: '상품을 파렛트에 적재 후 입고하면\n[사용함]으로 선택해 주세요.',
  QUANTITY_REQUIRED: '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.',
  TOTAL_QUANTITY_CALCULATION: '수량(BOX)*입수(BOX당)+수량(EA)로\n자동 계산됩니다.',

  RELEASE_CODE_PICKUP_EXPECTED_DATE: '컬리픽업, 밀크런픽업은 입고예정일시 기준 D-1일 17시까지만 선택 가능합니다.',
  DEFAULT_PICKUP_ADDRESS: '화주사 정보 관리 > 공급사 관리 메뉴에 등록된 출고지 정보입니다.',
  ETC_PICKUP_ADDRESS:
    '현재 작성중인 입고 요청건에 한해 사용할 출고지 정보입니다. 일회성으로 사용되며 저장되지 않습니다.',
  PICKUP_PALLET_QUANTITY: '입고센터별 실제 파렛트 수를 입력해주세요.',
  RECEIVE_EXPECTED_DATE: '[입고 방법] 선택 후 \n[입고예정일시]를 지정할 수 있습니다.',
  INBOUND_AREA_ADDRESS: '상품을 입고해야 하는 상세 주소입니다.\n입고대행서비스 이용 시 최종 입고지와 다를 수 있습니다.',

  FINAL_RECEIVE_EXPECTED_DATE: '입고대행서비스 이용 시\n입고예정일보다 1~2일 더 소요됩니다.',
  QUANTITY_DETAIL:
    '정상수량 : 검수를 통과한 입고 수량입니다.\n문제수량 : 검수 시 발견된 문제내용으로 인해\n입고할 수 없는 수량입니다.',
  RECEIVE_CONFIRM_DATE: '입고확정일시에 확정된\n입고 수량만큼 재고로 전환됩니다.',

  REPORT_STATUS_DESCRIPTION: `입고 건당 리포트가 2개 이상일 경우,\n우선순위에 따른 대표 상태가 노출됩니다.\n정확한 리포트 상태는 '입고 리포트 관리' 화면에서\n확인 바랍니다.`,
};

/**
 * [입고 관리] 에러 상태
 */
export const ERROR_VEHICLE_SIZE_REQUIRED_STATUS = {
  status: 'error',
  message: '차량 크기를 선택해주세요.',
} as TypeInputState;

/**
 * [입고 관리] LOT 번호 최대 길이
 */
export const LOT_NO_MAX_LENGTH = 20;

/**
 * [입고 관리] LOT 번호 정규식
 * @note 숫자, 영문, 특정 특수문자('-', '_')만 입력 가능
 */
export const LOT_NO_REGEX = /^[a-zA-Z0-9-_]*$/;

/**
 * [입고관리] 리포트 상태
 */
export const RECEIVE_REPORT_STATUS = {
  REGISTERED: { value: 'REGISTERED', text: '리포트 발행' },
  COMPLETED: { value: 'COMPLETED', text: '리포트 처리완료' },
  CANCELED: { value: 'CANCELED', text: '리포트 발행취소' },
  NONE: { value: 'NONE', text: '리포트 미발행' },
} as const;

export type TypeReceiveReportStatus = keyof typeof RECEIVE_REPORT_STATUS;

export const RECEIVE_REPORT_STATUS_BADGE_COLOR = {
  REGISTERED: 'info',
  COMPLETED: 'success',
  CANCELED: 'error',
  NONE: 'secondary',
} as const;

/**
 * [입고 관리] 리포트 유무
 */
export const RECEIVE_HAS_REPORT = {
  YES: { value: 'YES', text: '있음' },
  NO: { value: 'NO', text: '없음' },
} as const;
